// Landing Page Configuration

export const LANDING_CONFIG = {
  // Theme Colors
  colors: {
    primary: '#076cd8',
    secondary: '#4facfe',
    accent: '#00f2fe',
    gradients: {
      primary: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
      secondary: 'linear-gradient(45deg, #667eea, #764ba2)',
      tertiary: 'linear-gradient(45deg, #f093fb, #f5576c)',
      quaternary: 'linear-gradient(45deg, #4facfe, #00f2fe)',
      hero: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4))',
      features: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      portals: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      demo: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568',
      muted: '#718096',
      white: '#ffffff',
      error: '#e53e3e',
      success: '#38a169',
    },
  },

  // Animation Settings
  animations: {
    duration: {
      fast: '0.3s',
      normal: '0.5s',
      slow: '0.8s',
    },
    easing: {
      default: 'ease',
      inOut: 'ease-in-out',
      out: 'ease-out',
    },
  },

  // Layout Settings
  layout: {
    maxWidth: '1200px',
    padding: {
      mobile: '15px',
      tablet: '20px',
      desktop: '40px',
    },
    breakpoints: {
      mobile: '480px',
      tablet: '768px',
      desktop: '1024px',
    },
  },

  // Hero Section
  hero: {
    backgroundImage: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop',
    title: process.env.REACT_APP_TENANT_NAME || 'Telehealth Platform',
    subtitle: 'Connecting patients with healthcare professionals through secure, convenient telemedicine',
    description: 'Access quality healthcare from the comfort of your home with our comprehensive telehealth platform',
    buttons: [
      {
        text: 'Patient Portal',
        link: '/patient/login',
        type: 'primary',
        icon: 'PersonIcon',
      },
      {
        text: 'Provider Portal',
        link: '/admin/login',
        type: 'secondary',
        icon: 'LocalHospitalIcon',
      },
    ],
  },

  // Features Section
  features: {
    title: 'Why Choose Our Platform?',
    items: [
      {
        id: 'virtual-consultations',
        icon: 'VideoCallIcon',
        title: 'Virtual Consultations',
        description: 'High-quality video calls with healthcare professionals from anywhere, anytime',
        gradient: 'primary',
      },
      {
        id: 'secure-hipaa',
        icon: 'SecurityIcon',
        title: 'Secure & HIPAA Compliant',
        description: 'Your health information is protected with enterprise-grade security measures',
        gradient: 'secondary',
      },
      {
        id: 'availability',
        icon: 'ScheduleIcon',
        title: '24/7 Availability',
        description: 'Book appointments and access care when you need it most, day or night',
        gradient: 'tertiary',
      },
    ],
  },

  // Demo Section
  demo: {
    title: 'Ready to See It in Action?',
    subtitle: 'Request a personalized demo and discover how our telehealth platform can transform your healthcare delivery',
    emailRecipient: 'admin@hcgconsultinggroup.com',
    form: {
      fields: [
        {
          name: 'fullName',
          label: 'Full Name',
          type: 'text',
          required: true,
          placeholder: 'Enter your full name',
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          required: true,
          placeholder: 'Enter your email address',
        },
        {
          name: 'phone',
          label: 'Phone Number',
          type: 'tel',
          required: false,
          placeholder: 'Enter your phone number',
        },
        {
          name: 'company',
          label: 'Company/Organization',
          type: 'text',
          required: false,
          placeholder: 'Enter your company name',
        },
        {
          name: 'message',
          label: 'Additional Message',
          type: 'textarea',
          required: false,
          placeholder: 'Tell us about your specific needs or questions...',
          fullWidth: true,
        },
      ],
      validation: {
        email: /\S+@\S+\.\S+/,
        phone: /^[\+]?[1-9][\d]{0,15}$/,
      },
      buttonText: 'Request Demo Now',
      buttonIcon: 'EmailIcon',
    },
  },

  // Portals Section
  portals: {
    title: 'Access Your Portal',
    items: [
      {
        id: 'patients',
        icon: 'PeopleIcon',
        title: 'For Patients',
        description: 'Schedule appointments, access your medical records, and connect with healthcare providers through secure video consultations.',
        features: [
          'Book and manage appointments',
          'Access electronic health records',
          'Secure messaging with providers',
          'Prescription management',
        ],
        button: {
          text: 'Access Patient Portal',
          link: '/patient/login',
          icon: 'LocalHospitalIcon',
        },
        gradient: 'primary',
      },
      {
        id: 'providers',
        icon: 'LocalHospitalIcon',
        title: 'For Providers',
        description: 'Manage your practice, schedule patients, and provide remote care with our comprehensive provider tools.',
        features: [
          'Patient management system',
          'Appointment scheduling',
          'Clinical documentation',
          'Telemedicine consultations',
        ],
        button: {
          text: 'Access Provider Portal',
          link: '/admin/login',
          icon: 'PersonIcon',
        },
        gradient: 'secondary',
      },
    ],
  },

  // Form Validation Messages
  validation: {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    phone: 'Please enter a valid phone number',
    minLength: (min) => `Must be at least ${min} characters`,
    maxLength: (max) => `Must be no more than ${max} characters`,
  },

  // Loading States
  loading: {
    form: 'Submitting...',
    button: 'Loading...',
    image: 'Loading image...',
  },

  // Accessibility
  accessibility: {
    skipToContent: 'Skip to main content',
    logoAlt: 'Company Logo',
    iconAlt: (name) => `${name} icon`,
    loading: 'Loading content',
  },

  // SEO
  seo: {
    title: 'Telehealth Platform - Virtual Healthcare Solutions',
    description: 'Connect with healthcare professionals through secure, convenient telemedicine. Access quality healthcare from home.',
    keywords: 'telehealth, telemedicine, virtual healthcare, online consultation, healthcare platform',
  },
};

// Icon mapping for dynamic icon rendering
export const ICON_MAP = {
  VideoCallIcon: 'VideoCallIcon',
  SecurityIcon: 'SecurityIcon',
  ScheduleIcon: 'ScheduleIcon',
  PersonIcon: 'PersonIcon',
  LocalHospitalIcon: 'LocalHospitalIcon',
  EmailIcon: 'EmailIcon',
  PeopleIcon: 'PeopleIcon',
};

// Environment-based configuration
export const getEnvironmentConfig = () => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = process.env.NODE_ENV === 'production';
  
  return {
    isDevelopment,
    isProduction,
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    tenantLogo: process.env.REACT_APP_TENANT_LOGO,
    tenantName: process.env.REACT_APP_TENANT_NAME || 'Telehealth Platform',
    enableAnalytics: isProduction,
    enableAnimations: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
};

export default LANDING_CONFIG;